import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css'; // The base CSS for the slider is still needed

import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Row,
  FormGroup,
  Label,
  Input,
  Button
} from "reactstrap";

// --- CSS Styles ---
const filterComponentStyles = `
  .range-values {
    font-size: 0.9em;
    color: #495057;
    font-weight: 500;
  }
  .rc-slider-track {
    background-color: var(--bs-primary, #0d6efd);
  }
  .rc-slider-handle {
    border: 2px solid var(--bs-primary, #0d6efd);
    background-color: #fff;
    opacity: 1;
  }
  .rc-slider-handle:focus-visible,
  .rc-slider-handle:active {
    box-shadow: 0 0 0 0.25rem rgba(var(--bs-primary-rgb, 13, 110, 253), 0.25);
  }
  .select__control {
      border-color: #ced4da !important;
  }
  .select__control--is-focused {
      border-color: #86b7fe !important;
      box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25) !important;
  }
`;

const StyleInjector = () => <style>{filterComponentStyles}</style>;

const FilterComponent = ({ filters, onFilterChange, setIsSearch }) => {
  // Generates the initial state from the filters configuration
  const getInitialState = () => {
    return filters.reduce((acc, filter) => {
      // CHANGED: Handle range filters with separate minName and maxName
      if (filter.type === "range") {
        acc[filter.minName] = filter.defaultMin ?? filter.min ?? 0;
        acc[filter.maxName] = filter.defaultMax ?? filter.max ?? 100000;
      } else {
        acc[filter.name] = filter.defaultValue ?? "";
      }
      return acc;
    }, {});
  };

  const [filterValues, setFilterValues] = useState(getInitialState());
  const debounceTimer = useRef(null);

  // Debounced filter change handler
  const triggerFilterChange = (updatedValues) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      // Filter out empty strings, but allow 0 as a valid value
      const activeFilters = Object.fromEntries(
        Object.entries(updatedValues).filter(([_, val]) => val !== "" && val !== null)
      );
      onFilterChange(activeFilters);
    }, 700);
  };

  // Generic handler for simple inputs
  const handleChange = (name, value) => {
    setIsSearch(false);
    const updatedValues = { ...filterValues, [name]: value };
    setFilterValues(updatedValues);
    triggerFilterChange(updatedValues);
  };

  // CHANGED: Specific handler for the range slider now takes minName and maxName
  const handleRangeChange = (minName, maxName, rangeValues) => {
    setIsSearch(false);
    const [min, max] = rangeValues;
    const updatedValues = {
      ...filterValues,
      [minName]: min, // Use minName as the key
      [maxName]: max, // Use maxName as the key
    };
    setFilterValues(updatedValues);
    triggerFilterChange(updatedValues);
  };

  // Handler to clear all filters
  const handleClearFilters = () => {
    const initialValues = getInitialState();
    setFilterValues(initialValues);
    onFilterChange({}); // Notify parent that filters are cleared
    setIsSearch(false);
  };

  // Cleanup timer on component unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const renderFilter = (filter) => {
    // For non-range filters, we need a unique key. 'name' is appropriate.
    // For range filters, we can use the label or a combination of names.
    const key = filter.name || filter.label;

    switch (filter.type) {
      case "text":
        return (
          <Input
            type="text"
            id={filter.name}
            value={filterValues[filter.name] || ""}
            onChange={(e) => handleChange(filter.name, e.target.value)}
            placeholder={filter.placeholder || "Enter text..."}
          />
        );
      case "number":
        return (
          <Input
            type="number"
            id={filter.name}
            min={0}
            value={filterValues[filter.name] || ""}
            onChange={(e) => handleChange(filter.name, e.target.value === '' ? '' : Number(e.target.value))}
            placeholder={filter.placeholder || "Enter number..."}
          />
        );
      case "range":
        // CHANGED: Logic to read from separate min/max state values
        const min = filter.min || 0;
        const max = filter.max || 100000;
        // Read values using minName and maxName, providing fallbacks
        const currentMin = Number(filterValues[filter.minName] ?? min);
        const currentMax = Number(filterValues[filter.maxName] ?? max);

        return (
          <div className="px-2 pt-2"> {/* Padding for slider alignment */}
            <Slider
              range
              min={min}
              max={max}
              step={filter.step || 1000}
              value={[currentMin, currentMax]}
              // CHANGED: Pass minName and maxName to the handler
              onChange={(values) => handleRangeChange(filter.minName, filter.maxName, values)}
              allowCross={false}
            />
            <div className="d-flex justify-content-between mt-2 range-values">
              <span>{filter.formatter ? filter.formatter(currentMin) : `₹${currentMin.toLocaleString()}`}</span>
              <span>{filter.formatter ? filter.formatter(currentMax) : `₹${currentMax.toLocaleString()}`}</span>
            </div>
          </div>
        );
      case "select":
        return (
          <Select
            id={filter.name}
            options={filter.options}
            value={filter.options.find(opt => opt.value === filterValues[filter.name]) || null}
            onChange={(selected) => handleChange(filter.name, selected ? selected.value : "")}
            placeholder={filter.placeholder || "Select..."}
            classNamePrefix="select" // react-select class prefix
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <StyleInjector /> {/* This line injects the CSS into the page */}
      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <CardTitle tag="h5" className="mb-0">
            Filters
          </CardTitle>
          <div size="sm" onClick={handleClearFilters} className="p-0" style={{cursor:'pointer'}}>
            Clear All
          </div>
        </CardHeader>
        <CardBody>
          <Row>
            {filters.map((filter) => (
              // Use label or name for a stable key
              <Col sm={12} key={filter.name || filter.label}>
                <FormGroup>
                  {/* For range, the id might not match, but Label still needs it */}
                  <Label for={filter.name || filter.minName} className="fw-bold">
                    {filter.label}
                  </Label>
                  {renderFilter(filter)}
                </FormGroup>
              </Col>
            ))}
          </Row>
        </CardBody>
      </Card>
    </>
  );
};

export default FilterComponent;